/*
* Copyright Gregory Coburn 2020-2026, All Rights Reserved, See license for further details
*/
import { HttpParams } from '@angular/common/http';
import { Component, inject } from '@angular/core';
import { Field } from '@src/app/shared/field/Field';
import { AbstractPageComponent } from '@src/app/shared/form/abstract-page.component';
import { FieldSet, LAYOUT_OPTIONS } from '@src/app/shared/form/field-set/field-set.component';
import { FormButtonComponent } from '@src/app/shared/form/form-button/form-button.component';
import { FormNumberComponent } from '@src/app/shared/form/form-number/form-number.component';
import { FormPicklistComponent } from '@src/app/shared/form/form-picklist/form-picklist.component';
import { FormTextComponent } from '@src/app/shared/form/form-text/form-text.component';
import { FormConfig } from '@src/app/shared/form/FormConfig';
import { AgentDataService } from '@src/app/modules/agent/agent-data.service';
import { RequestAnalysis, RequestAnalysisService } from '@src/app/modules/crm/request-analysis/request-analysis.service';
import { ServiceRequest } from '@src/app/model/serviceRequest';
import { AbstractObject } from '@src/app/model/abstract-object';
import { FormDurationComponent } from '@src/app/shared/form/form-duration/form-duration.component';
import { FormPageComponent } from '@src/app/shared/form/form-page/form-page.component';
import { FormNavLinkComponent } from '@src/app/shared/form/form-nav-button/form-nav-link.component';
import { RequestAnalysisPageNavRoute } from '@src/app/routes/request-analysis-page-nav';
import { ServiceRequestPageNavRoute } from '@src/app/routes/service-request-page-nav';
import { FieldMaker } from '@src/app/shared/field/FieldMaker';
import { periodName, priorPeriod } from '@src/app/modules/crm/request-analysis/analysis-period';

export const WEEKLY_TYPE = { id: 'week', name: 'Weekly', openField: 'openWeek', closeField: 'closeWeek', workField: 'workWeek'};
export const MONTHLY_TYPE = {id: 'month', name: 'Monthly', openField: 'openMonth', closeField: 'closeMonth', workField: 'workMonth'};
export const PERIOD_TYPES = [WEEKLY_TYPE, MONTHLY_TYPE];

@Component({
    selector: 'app-request-analysis',
    templateUrl: './request-analysis.component.html',
    styleUrls: ['./request-analysis.component.scss'],
    imports: [FormPageComponent]
})
export class RequestAnalysisComponent extends AbstractPageComponent {
    dataSvc = inject(RequestAnalysisService);
    private agentDataSvc = inject(AgentDataService);


    detailsLinkField = FormNavLinkComponent.make('Details',
        RequestAnalysisPageNavRoute, this.getPeriodFilter.bind(this),
        { calculateValue: () => 'Details', docAlwaysInclude: true }
    );

    requestsOpenedLink = FormNavLinkComponent.make('Requests Opened',
        ServiceRequestPageNavRoute, this.getOpenedFilter.bind(this),
        { value: 'opened', cellOpts: {style: 'text-align: right; padding-right: 0px', thColspan: 2}}
    );

    requestsClosedLink = FormNavLinkComponent.make('Requests Closed',
        ServiceRequestPageNavRoute, this.getClosedFilter.bind(this),
        { value: 'closed', cellOpts: {style: 'text-align: right; padperiodding-right: 0px', thColspan: 2 } }
    );

    requestsStillOpenLink = FormNavLinkComponent.make('Still Open',
        ServiceRequestPageNavRoute, this.getStillOpenFilter.bind(this),
        { value: 'stillOpen', cellOpts: {style: 'text-align: center' } }
    );

    workTimeField = FormDurationComponent.make('Time Logged', 'workTime', {
        readonly: true, cellOpts: { style: 'text-align: right', thColspan: 2 }
    });

    openedIndicator = FormButtonComponent.make('', '', {
        name: 'openedIndicator', type: 'icon', label: '',
        calculateValue: (o: RequestAnalysis) => {
            o = (o as RequestAnalysis)
            if (!o.previous) {
                return '';
            } else if (o.opened > o.previous.opened) {
                return 'north'
            } else if (o.opened < o.previous.opened) {
                return 'south';
            }
        }
    }).setTableIconStyle((o) => {
        const x = (o as RequestAnalysis);
        if (!x.previous) {
            return {};
        } else if (x.opened > x.previous.opened) {
            return {'color': 'green', 'font-size': '20px', 'padding-top': '17px'};
        } else if (x.opened < x.previous.opened) {
            return {'color': 'red', 'font-size': '20px','padding-top': '17px'};
        }
    }).override({cellOpts: {thStyle: 'display:none', style: 'padding-left: 0px'}});

    closedIndicator = FormButtonComponent.make('', '', {
        name: 'closedIndicator', type: 'icon', label: '',
        calculateValue: (o: RequestAnalysis) => {
            o = (o as RequestAnalysis)
            if (!o.previous) {
                return '';
            } else if (o.closed > o.previous.closed) {
                return 'north'
            } else if (o.closed < o.previous.closed) {
                return 'south';
            }
        }
    }).setTableIconStyle((o) => {
        const x = (o as RequestAnalysis);
        if (!x.previous) {
            return {};
        } else if (x.closed > x.previous.closed) {
            return {'color': 'green'}
        } else if (x.closed < x.previous.closed) {
            return {'color': 'red'};
        }
    }).override({cellOpts: {thStyle: 'display:none', style: 'padding-left: 0px'}});

    daysToCloseField = FormNumberComponent.make('Average Days To Close', 'daysToClose',
        {formatParms: '1.0-2'},{cellOpts: {thColspan: 2, style: 'text-align: right; padding-right: 0px'}}
    );

    daysToCloseIndicator = FormButtonComponent.make('', '', {
        name: 'daysToCloseIndicator', type: 'icon', label: '',
        calculateValue: (o: RequestAnalysis) => {
            o = (o as RequestAnalysis)
            if (!o.previous) {
                return '';
            } else if (o.daysToClose < o.previous.daysToClose) {
                return 'north'
            } else if (o.daysToClose > o.previous.daysToClose) {
                return 'south';
            }
        }
    }).setTableIconStyle((o) => {
        const x = (o as RequestAnalysis);
        if (!x.previous) {
            return {};
        } else if (x.daysToClose < x.previous.daysToClose) {
            return { 'color': 'green' };
        } else if (x.daysToClose > x.previous.daysToClose) {
            return { 'color': 'red' };
        }
    }).override({cellOpts: {thStyle: 'display:none', style: 'padding-left: 0px'}});

    workedIndicator = FormButtonComponent.make('', '', {
        name: 'workedIndicator', type: 'icon', label: '',
        calculateValue: (o: RequestAnalysis) => {
            o = (o as RequestAnalysis)
            if (!o.previous) {
                return '';
            } else if (o.workTime > o.previous.workTime) {
                return 'north'
            } else if (o.workTime < o.previous.workTime) {
                return 'south';
            }
        }
    }).setTableIconStyle((o) => {
        const x = (o as RequestAnalysis);
        if (!x.previous) {
            return {};
        } else if (x.workTime > x.previous.workTime) {
            return { 'color': 'green' };
        } else if (x.workTime < x.previous.workTime) {
            return { 'color': 'red' };
        }
    }).override({ cellOpts: { thStyle: 'display:none', style: 'padding-left: 0px' } });

    omcField = FormPicklistComponent.make('OMC', 'omcId', null, { items: [] },
        { visible: Field.noShow, docAlwaysInclude: true });

    constructor() {
        super();
        this.agentDataSvc.getOmcList().subscribe(omcs => this.omcField.setPicklistItems(omcs));
    }
    periodField = FormPicklistComponent.make('Period', 'period', null, {items: []})
    countField = FormPicklistComponent.make('Total Open', 'totalOpen', null, 
        {items: [{id:99, name: 'Total'}]}, {calculateValue: () => 99, visible: Field.noShow})
    config = new FormConfig({
        navRoute: RequestAnalysisPageNavRoute,
        docPath: 'omc-professional/requests/request-analysis',
        title: $localize`Analysis`,
        help: $localize`Analysis of requests`,
        fieldSet: new FieldSet({
            fields: [
                FormPicklistComponent.make('Period Type', 'periodType', null, {
                    items: PERIOD_TYPES
                }, { visible: Field.noShow }),
                this.omcField,
                this.detailsLinkField,
                this.periodField,
                FormTextComponent.make('Period', 'periodDesc', {cellOpts: {style: 'min-width: 20em'}}),
                //FormTextAreaComponent.make('Period', 'period'),
                //FormNumberComponent.make('Requests Opened', 'opened'),
                this.requestsOpenedLink,
                this.openedIndicator,
                //FormNumberComponent.make('Requests Closed', 'closed'),
                this.requestsClosedLink,
                this.closedIndicator,
                this.daysToCloseField,
                this.daysToCloseIndicator,
                this.requestsStillOpenLink,
                this.workTimeField,
                this.workedIndicator,
                this.countField,
                FieldMaker.spacer()
            ],
            formLayout: LAYOUT_OPTIONS.singleCol,
        }),
        service: this.dataSvc,
        mode: 'list',
        beforeList: this.beforeList.bind(this),
    });

    private getStillOpenFilter(o : RequestAnalysis) {
        const stillOpen = this.getOpenedFilter(o);
        stillOpen['srStatus'] = ServiceRequest.srOpen.id

        return stillOpen
    }

    private getOpenedFilter(o : RequestAnalysis) {
        const opened = {};
        if (o.omcId) {
            opened['omcId'] = o.omcId;
        }
        const type = this.getPeriodsType(o.period);
        opened[type.openField] = o.period;
        return opened;
    }

    private getClosedFilter(o : RequestAnalysis) {
        const closed = {};
        if (o.omcId) {
            closed['omcId'] = o.omcId;
        }
        const type = this.getPeriodsType(o.period);
        closed[type.closeField] = o.period;
        return closed
    }

    private getPeriodFilter(o : RequestAnalysis) {

        const type = this.getPeriodsType(o.period);
        return {periodType: type.id, period: o.period}

    }

    getPeriodsType(s: string) {
        if (s.indexOf('w') > 0) {
            return WEEKLY_TYPE;
        } else {
            return MONTHLY_TYPE;
        }
    }

    showOmcField(parms: HttpParams) {
        if (!parms.has('omcId') && parms.has('period')) {
            this.omcField.visible = Field.showAll;
        } else {
            this.omcField.visible = Field.noShow;
        }
    }

    showDetailsLink(parms: HttpParams) {
        const alreadyInOnePeriod = parms.has('period');
        this.detailsLinkField.visible = alreadyInOnePeriod ? Field.noShow : Field.showAll;
    }

    parseAsMap(input) : Map<string, RequestAnalysis>{
        const map = new Map<string, RequestAnalysis>();

        const openData: RequestAnalysis[] = input['openData'];
        const closeData: RequestAnalysis[] = input['closeData'];
        const workData: RequestAnalysis[] = input['workData'];

        openData.forEach( (open) => {
            map.set(open.period + open.omcId, { ...new RequestAnalysis(), ...open});
        });

        closeData.forEach((closed) => {
            let item = { ...new RequestAnalysis(), ...closed};
            if (!map.has(closed.period+closed.omcId)) {
                map.set(closed.period+closed.omcId, item);
            } else {
                item = map.get(closed.period+closed.omcId);
                item.closed = closed.closed;
                item.daysToClose = closed.daysToClose;
            }
        });

        workData.forEach((worked) => {
            worked.workTime = +worked.workTime;
            let item = { ...new RequestAnalysis(), ...worked };
            if (!map.has(worked.period + worked.omcId)) {
                map.set(worked.period + worked.omcId, item);
            } else {
                item = map.get(worked.period + worked.omcId);
                item.workTime = worked.workTime;
            }
        });



        map.forEach((value) => {
            value.daysToClose = +value.daysToClose;
            value.stillOpen = +value.stillOpen;
            value.periodDesc = periodName(value.period);
        });

        return map;
    }

    mapPreviousPeriods(mapItems: Map<string, RequestAnalysis>, mapPrev: Map<string, RequestAnalysis>) {
        mapItems.forEach((value) => {
            const ppName = priorPeriod(value.period);
            if (mapPrev.has(ppName + value.omcId)) {
                value.previous = mapPrev.get(ppName+value.omcId);
            }
        });
    }

    beforeList(input: RequestAnalysis[], parms: HttpParams) {
        const map = this.parseAsMap(input);
        this.showOmcField(parms);
        this.showDetailsLink(parms);

        if (parms.has('period')) {
            const pp = priorPeriod(parms.get('period'));
            const newParms = parms.set('period', pp);
            this.dataSvc.get(false, newParms).subscribe ( (result) => {
                const prevMap = this.parseAsMap(result);
                this.mapPreviousPeriods(map, prevMap);
            })
        } else {
            this.mapPreviousPeriods(map, map);
        }
        const ary = Array.from(map.values());

        this.resetPeriodPicklist(ary);

        return ary.sort((a, b) => a.period.localeCompare(b.period)).reverse();
    }

    resetPeriodPicklist(ary: RequestAnalysis[]) {
        const newList: AbstractObject[] = [];
        ary.forEach( o => {
            if (!newList.find( a => a.id === o.period)) {
                newList.push({ id: o.period, name: o.period})
            }
        });
        this.mergeList(newList);

        if (newList.length === 1) {
            const pp = priorPeriod(newList[0].id as string);
            //newList.push({id: pp, name: this.getPeriodDesc({period: pp} as RequestAnalysis)});
            newList.push({id: pp, name: pp});
        }

        const sortedItems = newList.sort( (a,b) => a.name.localeCompare(b.name)).reverse();
        this.periodField.setPicklistItems(sortedItems);

    }

    mergeList(newList: AbstractObject[]) {
        if (newList.length === 0) {
            return;
        }
        const periodType = this.getPeriodsType(newList[0].id as string);
        const items = this.periodField.picklist.items;
        if (items && items[0]) { // If there are existing items of same period type...
            if (this.getPeriodsType(items[0].id as string) === periodType) {
                items.forEach( o => {
                    if (!newList.find( ni => ni.id === o.id)) {
                        newList.push(o);
                    }
                });
            }
        }
    }
}
